<template>
  <div class="dropdown-input-container" ref="container">
    <label v-if="label" class="dropdown-input-label">{{ label }}<span v-if="required" class="required-indicator">*</span><vue-feather v-if="tooltip" type="info" size="14" class="label-tooltip-icon" v-tooltip="tooltip" /></label>
    <div class="dropdown-input" :class="inputClass" @click="toggle" tabindex="0" @keydown="handleKeydown"
      @blur="handleBlur" ref="trigger">
      <transition name="ai-prefill" mode="out-in">
        <!-- Normal dropdown display -->
        <div v-if="!aiPrefill" class="dropdown-input-content" key="input">
          <span class="dropdown-input-value"
            :class="{ 'dropdown-input-placeholder': modelValue === null || modelValue === undefined || modelValue === '' }">
            {{ displayValue }}
          </span>
          <span v-if="loading" class="dropdown-input-spinner"></span>
          <vue-feather v-else :type="isOpen ? 'chevron-up' : 'chevron-down'" size="18" class="dropdown-input-icon" />
        </div>
        <!-- AI Prefill chip -->
        <div v-else class="ai-prefill-chip" key="prefill">
          <div class="ai-prefill-chip-content">
            <img src="@/assets/icons/stars.png" alt="AI" class="ai-prefill-icon" />
            <span class="ai-prefill-value">{{ aiPrefill.value }}</span>
          </div>
          <button class="ai-prefill-dismiss" @click.stop="dismissPrefill" type="button" title="Dismiss AI suggestion">
            <vue-feather type="x" size="14" />
          </button>
        </div>
      </transition>
    </div>
    <!-- Dropdown -->
    <FloatingDropdown ref="floatingDropdown" :open="isOpen" :trigger-ref="$refs.trigger" @dismiss="close">
      <div class="select-dropdown-menu">
        <div v-if="searchable" class="dropdown-search-wrapper">
          <input ref="searchInput" v-model="searchQuery" class="dropdown-search-input" placeholder="Search..."
            autocomplete="nope" data-lpignore="true" data-form-type="other"
            @keydown.stop="handleSearchKeydown" @click.stop />
        </div>
        <div v-for="(option, index) in filteredOptions" :key="option.value" class="dropdown-item" :class="{
          'dropdown-item--selected': option.value === modelValue,
          'dropdown-item--focused': index === focusedIndex,
          'dropdown-item--has-sublabel': option.sublabel,
          'dropdown-item--disabled': option.disabled
        }" @click.stop="selectOption(option)" @mouseenter="focusedIndex = index"
          v-tooltip="option.disabled && option.tooltip ? option.tooltip : ''">
          <div class="dropdown-item-content">
            <span class="dropdown-item-label">{{ option.label }}<vue-feather v-if="option.icon" :type="option.icon" size="13" class="dropdown-item-icon" v-tooltip="option.iconTooltip || ''" /></span>
            <span v-if="option.sublabel" class="dropdown-item-sublabel">{{ option.sublabel }}</span>
          </div>
          <vue-feather v-if="option.value === modelValue" type="check" size="16" class="dropdown-item-check" />
        </div>
      </div>
    </FloatingDropdown>
    <span v-if="error" class="dropdown-input-error">{{ error }}</span>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'
import { FloatingDropdown } from '@/design'

export default {
  name: 'DropdownInput',
  components: {
    'vue-feather': VueFeather,
    FloatingDropdown
  },
  props: {
    modelValue: {
      // Boolean: settings views bind Enabled/Disabled dropdowns to boolean flags
      type: [String, Number, Boolean],
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: 'Select...'
    },
    // Array of { value, label, sublabel? } objects
    options: {
      type: Array,
      required: true,
      validator: (options) => options.every(opt => 'value' in opt && 'label' in opt)
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    tooltip: {
      type: String,
      default: ''
    },
    // AI prefill configuration: { value: String }
    // The icon is always the AI stars icon
    aiPrefill: {
      type: Object,
      default: null
    },
    searchable: {
      type: Boolean,
      default: false
    },
    // The options are still loading: a spinner replaces the chevron and the dropdown can't open
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isOpen: false,
      focusedIndex: -1,
      searchQuery: ''
    }
  },
  computed: {
    inputClass() {
      return {
        'dropdown-input--open': this.isOpen,
        'dropdown-input--disabled': this.disabled || this.loading,
        'dropdown-input--error': this.error,
        'dropdown-input--prefill': !!this.aiPrefill
      }
    },
    filteredOptions() {
      if (!this.searchable || !this.searchQuery.trim()) return this.options
      const q = this.searchQuery.toLowerCase()
      return this.options.filter(opt =>
        (opt.label && opt.label.toLowerCase().includes(q)) ||
        (opt.sublabel && opt.sublabel.toLowerCase().includes(q))
      )
    },
    displayValue() {
      if (this.modelValue === null || this.modelValue === undefined || this.modelValue === '') return this.placeholder
      const selected = this.options.find(opt => opt.value === this.modelValue)
      return selected ? selected.label : this.placeholder
    },
  },
  watch: {
    searchQuery() {
      this.focusedIndex = 0
    }
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick)
  },
  unmounted() {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    toggle() {
      if (this.disabled || this.loading || this.aiPrefill) return
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.focusedIndex = this.options.findIndex(opt => opt.value === this.modelValue)
        if (this.focusedIndex === -1) this.focusedIndex = 0
        if (this.searchable) {
          this.$nextTick(() => { this.$refs.searchInput?.focus() })
        }
      }
    },
    close() {
      this.isOpen = false
      this.focusedIndex = -1
      this.searchQuery = ''
    },
    selectOption(option) {
      if (option.disabled) return
      // Dismiss AI prefill when user selects a different option
      if (this.aiPrefill) {
        this.$emit('dismiss-prefill')
      }
      this.$emit('update:modelValue', option.value)
      this.$emit('change', option.value)
      this.close()
      this.$refs.trigger.focus()
    },
    dismissPrefill() {
      this.$emit('dismiss-prefill')
    },
    handleOutsideClick(event) {
      if (!this.isOpen) return
      const container = this.$refs.container
      if (container && !container.contains(event.target)) {
        this.close()
      }
    },
    handleBlur() {
      // Only close if focus moves outside the container and the teleported dropdown
      setTimeout(() => {
        const container = this.$refs.container
        const active = document.activeElement
        if (container && !container.contains(active) && !this.$refs.floatingDropdown?.containsElement(active)) {
          this.close()
        }
      }, 100)
    },
    handleSearchKeydown(event) {
      switch (event.key) {
        case 'Enter':
          event.preventDefault()
          if (this.focusedIndex >= 0 && this.focusedIndex < this.filteredOptions.length) {
            const focused = this.filteredOptions[this.focusedIndex]
            if (!focused.disabled) this.selectOption(focused)
          }
          break
        case 'Escape':
          event.preventDefault()
          this.close()
          this.$refs.trigger.focus()
          break
        case 'ArrowDown':
          event.preventDefault()
          if (this.focusedIndex < this.filteredOptions.length - 1) {
            let next = this.focusedIndex + 1
            while (next < this.filteredOptions.length && this.filteredOptions[next].disabled) next++
            if (next < this.filteredOptions.length) this.focusedIndex = next
          }
          break
        case 'ArrowUp':
          event.preventDefault()
          if (this.focusedIndex > 0) {
            let prev = this.focusedIndex - 1
            while (prev >= 0 && this.filteredOptions[prev].disabled) prev--
            if (prev >= 0) this.focusedIndex = prev
          }
          break
      }
    },
    handleKeydown(event) {
      if (this.disabled || this.loading) return

      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault()
          if (this.isOpen && this.focusedIndex >= 0) {
            const focused = this.filteredOptions[this.focusedIndex]
            if (!focused.disabled) this.selectOption(focused)
          } else {
            this.toggle()
          }
          break
        case 'Escape':
          event.preventDefault()
          this.close()
          break
        case 'ArrowDown':
          event.preventDefault()
          if (!this.isOpen) {
            this.toggle()
          } else {
            let next = this.focusedIndex + 1
            while (next < this.filteredOptions.length && this.filteredOptions[next].disabled) next++
            if (next < this.filteredOptions.length) this.focusedIndex = next
          }
          break
        case 'ArrowUp':
          event.preventDefault()
          if (this.isOpen) {
            let prev = this.focusedIndex - 1
            while (prev >= 0 && this.filteredOptions[prev].disabled) prev--
            if (prev >= 0) this.focusedIndex = prev
          }
          break
        case 'Tab':
          this.close()
          break
      }
    },
    focus() {
      this.$refs.trigger.focus()
    }
  },
  emits: ['update:modelValue', 'change', 'dismiss-prefill']
}
</script>

<style scoped>
.dropdown-input-container {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dropdown-input-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 4px;
}

.required-indicator {
  color: #c62828;
  margin-left: 2px;
}

.label-tooltip-icon {
  color: #9ca3af;
  cursor: help;
  flex-shrink: 0;
}

.dropdown-input {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 45px;
  padding: 0 14px;
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-sizing: border-box;
  cursor: pointer;
  transition: all 0.15s ease;
  outline: none;
  user-select: none;
}

.dropdown-input:hover:not(.dropdown-input--disabled):not(.dropdown-input--error) {
  border-color: #9ca3af;
}

.dropdown-input:focus:not(.dropdown-input--error),
.dropdown-input--open:not(.dropdown-input--error) {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.dropdown-input--disabled {
  background: #fafafa;
  cursor: not-allowed;
}

.dropdown-input--error {
  border-color: #dc2626;
}

.dropdown-input--error:focus,
.dropdown-input--error.dropdown-input--open {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

/* AI Prefill chip styling */
.dropdown-input--prefill {
  background: transparent;
  border-color: transparent;
  padding: 0;
}

.dropdown-input--prefill:hover {
  border-color: transparent;
}

.dropdown-input-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
}

/* AI Prefill transition */
.ai-prefill-enter-active {
  animation: ai-prefill-in 0.25s ease-out;
}

.ai-prefill-leave-active {
  animation: ai-prefill-out 0.2s ease-in;
}

@keyframes ai-prefill-in {
  0% {
    opacity: 0;
    transform: translateY(-8px) scale(0.96);
  }

  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes ai-prefill-out {
  0% {
    opacity: 1;
  }

  40% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

@keyframes ai-prefill-border-out {
  0% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

.ai-prefill-chip.ai-prefill-leave-active::before {
  animation: ai-prefill-border-out 0.08s ease-out forwards;
}

.ai-prefill-chip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  min-height: 45px;
  padding: 0 6px 0 14px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(99, 102, 241, 0.06) 50%, rgba(168, 85, 247, 0.08) 100%);
  border: 2px solid transparent;
  border-radius: 4px;
  background-clip: padding-box;
  position: relative;
  box-sizing: border-box;
}

.ai-prefill-chip::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 6px;
  padding: 2px;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #a855f7 100%);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  pointer-events: none;
}

.ai-prefill-chip-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.ai-prefill-icon {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  filter: drop-shadow(0 0 4px rgba(139, 92, 246, 0.4));
}

.ai-prefill-value {
  font-size: 0.9375rem;
  font-weight: 500;
  color: #4c1d95;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-prefill-dismiss {
  width: 28px;
  height: 28px;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #7c3aed;
  transition: all 0.15s ease;
  flex-shrink: 0;
  margin-left: 8px;
}

.ai-prefill-dismiss:hover {
  background: rgba(139, 92, 246, 0.2);
  border-color: rgba(139, 92, 246, 0.4);
  color: #6d28d9;
  transform: scale(1.05);
}

.ai-prefill-dismiss:active {
  transform: scale(0.95);
}

.dropdown-input-value {
  font-size: 1rem;
  font-weight: 400;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
}

.dropdown-input-placeholder {
  color: #9ca3af;
}

.dropdown-input-icon {
  color: #9ca3af;
  flex-shrink: 0;
  margin-left: 8px;
  transition: color 0.15s ease;
}

/* Same spinner as BaseButton's loading state, in the chevron's color and place */
.dropdown-input-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid #9ca3af;
  border-top-color: transparent;
  border-radius: 50%;
  flex-shrink: 0;
  margin-left: 8px;
  animation: dropdown-input-spin 0.6s linear infinite;
}

@keyframes dropdown-input-spin {
  to { transform: rotate(360deg); }
}

.dropdown-input:focus .dropdown-input-icon,
.dropdown-input--open .dropdown-input-icon {
  color: var(--color-primary, #6a16f0);
}

.dropdown-input-error {
  font-size: 0.8125rem;
  color: #dc2626;
}

</style>

<style>
/* Dropdown Menu - not scoped so it applies when teleported */
.select-dropdown-menu .dropdown-search-wrapper {
  padding: 4px 4px 0;
}

.select-dropdown-menu .dropdown-search-input {
  width: 100%;
  padding: 8px 10px;
  font-size: 0.875rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  outline: none;
  box-sizing: border-box;
  margin-bottom: 4px;
}

.select-dropdown-menu .dropdown-search-input:focus {
  border-color: var(--color-primary, #6a16f0);
}

.select-dropdown-menu {
  background: #FFF;
  border: 1px solid #e5e7eb;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  z-index: 10000;
  max-height: min(var(--fd-max-height, 9999px), 240px);
  overflow-y: auto;
  padding: 4px;
  text-align: left;
}

.select-dropdown-menu .dropdown-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 400;
  color: #374151;
  transition: background 0.1s ease;
  text-align: left;
}

.select-dropdown-menu .dropdown-item:hover,
.select-dropdown-menu .dropdown-item--focused {
  background: #f3f4f6;
}

.select-dropdown-menu .dropdown-item--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.select-dropdown-menu .dropdown-item--disabled:hover,
.select-dropdown-menu .dropdown-item--disabled.dropdown-item--focused {
  background: transparent;
}

.select-dropdown-menu .dropdown-item--selected {
  color: var(--color-primary, #6a16f0);
  font-weight: 500;
}

.select-dropdown-menu .dropdown-item-check {
  color: var(--color-primary, #6a16f0);
  flex-shrink: 0;
}

.select-dropdown-menu .dropdown-item-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.select-dropdown-menu .dropdown-item-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 5px;
}

.select-dropdown-menu .dropdown-item-icon {
  color: #f59e0b;
  flex-shrink: 0;
  cursor: help;
}

.select-dropdown-menu .dropdown-item-sublabel {
  font-size: 0.8125rem;
  color: #6b7280;
  font-weight: 400;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-dropdown-menu .dropdown-item--has-sublabel {
  padding: 8px 12px;
}
</style>
